import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { css, CSSResult, html, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DataTableWrapper } from '../../../../webmodule-common/other/ui/datatable-view';
import { FabricationPurchaseOrderItem, PurchaseItemType } from '../../../api/dealer-api-interface-all';
import {
  filterAndSortPurchaseOrderItems,
  purchaseOrderItemDimensionsText,
  purchaseOrderItemPackRoll,
  purchaseOrderItemPackRollHint
} from '../data/fabrication-purchase-order-item-view';
import { fireQuickErrorToast } from '../../../../webmodule-common/other/toast-away';
import { getMaterialTypeLabel, getSourceLabel } from '../data/fabrication-purchase-order-helper-functions';
import { LangFabricationPurchaseOrder } from '../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { moneyToStr, stringToCurrencyCode } from '../../../../webmodule-common/other/currency-formatter';
import { RequestPage, ResultPaginated } from '../../../../webmodule-common/other/ui/RequestPage';
import { v6SupportsVersion, v6Util, v6VersionMap } from '../../../../webmodule-common/interop/interfaces/v6-config';
import {
  WebmoduleChangeEvent,
  WebmoduleInput,
  WebmoduleRadioGroup,
  WebmoduleSelectEvent,
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';

/**
 * Styles injected into the <webmodule-table>'s shadow root via its `customStyles`
 * hook. The table renders cell content inside its shadow DOM, where the page
 * stylesheet cannot reach, so the quantity cell's layout comes from here.
 */
const fabricationPurchaseOrderItemsTableStyles = css`
  /* Width of the quantity editors, shared by the count and length inputs (it was one
     colpx-136 class until the class stopped reaching into the table's shadow DOM). */
  .po-quantity-input {
    width: 136px;
  }

  /* The hint used to sit 0.5rem clear of the editor via ms-2, which no longer reaches
     in here either. The cell is a flex row with a 0.25rem gap, so top it up by 0.25rem. */
  .po-quantity-hint {
    margin-left: 0.25rem;
  }
`;

// Radio value for the "no type filter" option. A single constant so the literal
// can't drift between the three sites that read/write it (e.g. a stray 'aIl').
const ALL_MATERIAL_TYPES = 'all';

export interface FabricationPurchaseOrderItemsTableOptions {
  items: () => FabricationPurchaseOrderItem[];
  isReadonly: () => boolean;
  supplierId: string;
  onQuantityChange: (item: FabricationPurchaseOrderItem, newQty: number) => Promise<void>;
  onRemoveLine: (item: FabricationPurchaseOrderItem) => Promise<void>;
  // Supplier pricing only exists once the PO is issued; the column is shown only then.
  showPricing: () => boolean;
  currency: () => string;
}

@customElement('wm-fabricationpurchaseorderitemstable')
export class FabricationPurchaseOrderItemsTable extends DataTableWrapper<FabricationPurchaseOrderItem> {
  options: FabricationPurchaseOrderItemsTableOptions;

  // Material-type radio filter: null = All. Reactive so the radio reflects the selection;
  // the row filtering happens in getRowsFromServer and the Type column is dropped in getColumns.
  @state()
  private materialTypeFilter: PurchaseItemType | null = null;

  constructor(options: FabricationPurchaseOrderItemsTableOptions) {
    super();
    this.options = options;
  }

  // Why filter/sort here? There is no server call - the detail view holds every PO line
  // in memory, so this "FromServer" hook is the table's only data seam. The component just
  // hands us the requested sort field/direction (sortField matches a column fieldName); it
  // does not order the rows itself.
  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<FabricationPurchaseOrderItem>> {
    const items = filterAndSortPurchaseOrderItems(this.options.items(), {
      filterTerm: this.filterValue,
      sortField: request.sortField,
      sortAscending: request.sortAsc,
      typeLabel: getMaterialTypeLabel,
      sourceLabel: getSourceLabel,
      groupBySource: true,
      materialTypeFilter: this.materialTypeFilter
    });
    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  override disablePagination(): boolean {
    return true;
  }

  override customStyles(): CSSResult {
    return fabricationPurchaseOrderItemsTableStyles;
  }

  enableFiltering(): boolean {
    return true;
  }

  updateFilter(searchTerm: string | null) {
    this.filterValue = searchTerm;
  }

  enableAdvancedFiltering(): boolean {
    return true;
  }

  // Material-type radio filter rendered next to the text search (col-7 slot), mirroring the
  // quote list's All/My author filter. "Glass" maps to the Fill type.
  advancedFilterTemplate(): TemplateResult {
    const radio = (value: string, text: string) => html`<webmodule-radio value="${value}">${text}</webmodule-radio>`;
    return html`
      <webmodule-radio-group
        name="po-item-type-filter"
        class="fabrication-purchase-order-type-filter"
        value="${this.materialTypeFilter ?? ALL_MATERIAL_TYPES}"
        inline
        size="small"
        @webmodule-change=${(e: WebmoduleChangeEvent) => this.onMaterialTypeFilterChanged(e)}
      >
        ${radio(ALL_MATERIAL_TYPES, LangFabricationPurchaseOrder.typeFilterAll())}
        ${radio(`${PurchaseItemType.Extrusion}`, LangFabricationPurchaseOrder.typeFilterExtrusions())}
        ${radio(`${PurchaseItemType.Gasket}`, LangFabricationPurchaseOrder.typeFilterGaskets())}
        ${radio(`${PurchaseItemType.Component}`, LangFabricationPurchaseOrder.typeFilterComponents())}
        ${radio(`${PurchaseItemType.Fill}`, LangFabricationPurchaseOrder.typeFilterGlass())}
      </webmodule-radio-group>
    `;
  }

  private async onMaterialTypeFilterChanged(e: WebmoduleChangeEvent): Promise<void> {
    const target = e.target as WebmoduleRadioGroup | null;
    if (!target) return;
    this.materialTypeFilter = target.value === ALL_MATERIAL_TYPES ? null : (Number(target.value) as PurchaseItemType);
    // Columns are bound once at connect time, so re-apply them to add/drop the Type column,
    // then re-fetch so the rows reflect the new filter.
    this.connectDataTable();
    await this.refreshData();
  }

  getDefaultSortField(): string | undefined {
    return 'itemType';
  }

  getDefaultSortAsc(): boolean {
    return true;
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Why is the sizing all here? The equivalent flex rules in dealer_ui.scss are gone - global
    // CSS cannot reach the new table's shadow DOM - so a fixed `width` pins a column and a lone
    // `minWidth` marks the flexible pair (Description/Finish) that absorbs the slack.
    const typeColumn: WebModuleTableColumnDef = {
      title: () => LangFabricationPurchaseOrder.typeColumn(),
      fieldName: 'itemType',
      sortable: true,
      width: '108px',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const line = item as FabricationPurchaseOrderItem;
        return html`${getMaterialTypeLabel(line.itemType)}`;
      }
    };
    return [
      // Drop the Type column when the rows are already filtered to a single type (redundant).
      ...(this.materialTypeFilter == null ? [typeColumn] : []),
      {
        title: () => LangFabricationPurchaseOrder.descriptionColumn(),
        fieldName: 'description',
        sortable: true,
        minWidth: '154px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const line = item as FabricationPurchaseOrderItem;
          const code = `${line.libraryCode ? `${line.libraryCode} ` : ''}${line.code}`;
          return this.valueWithMutedSuffix(line.resourceDescription, code);
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.finishColumn(),
        fieldName: 'finish',
        sortable: true,
        minWidth: '144px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const line = item as FabricationPurchaseOrderItem;
          return this.valueWithMutedSuffix(line.finishDescription, line.finish);
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.quantityColumn(),
        fieldName: 'quantity',
        width: '240px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const line = item as FabricationPurchaseOrderItem;
          return this.quantityCell(line);
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.dimensionsColumn(),
        fieldName: 'dimensions',
        width: '176px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const line = item as FabricationPurchaseOrderItem;
          return html`${purchaseOrderItemDimensionsText(line)}`;
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.sourceColumn(),
        fieldName: 'source',
        width: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const line = item as FabricationPurchaseOrderItem;
          return html`${getSourceLabel(line.source)}`;
        }
      },
      // Per-line supplier price, only once the PO is issued (Draft lines have zero price).
      ...(this.options.showPricing()
        ? [
            {
              title: () => LangFabricationPurchaseOrder.priceColumn(),
              fieldName: 'netSellPrice',
              sortable: true,
              width: '110px',
              displayValue: (_table: WebModuleTable, item: unknown) => {
                const line = item as FabricationPurchaseOrderItem;
                return html`${moneyToStr(line.netSellPrice, stringToCurrencyCode(this.options.currency()))}`;
              }
            } satisfies WebModuleTableColumnDef
          ]
        : []),
      {
        title: () => '',
        fieldName: 'xx',
        width: '64px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const line = item as FabricationPurchaseOrderItem;
          return this.ellipsisMenu(line);
        }
      }
    ];
  }

  // Renders a primary value with an optional muted suffix (e.g. description first,
  // then the code/finish reference). Non-breaking spaces flank the hyphen because the
  // muted span is a flex item, whose leading regular whitespace would otherwise collapse.
  private valueWithMutedSuffix(primary: string, suffix: string): TemplateResult {
    return html`${primary}${suffix ? html`<span class="muted">&nbsp;-&nbsp;${suffix}</span>` : ''}`;
  }

  private quantityCell(line: FabricationPurchaseOrderItem): TemplateResult {
    const hint = purchaseOrderItemPackRollHint(line);
    const info = this.packRollInfo(line);
    if (this.options.isReadonly()) {
      // quantityDisplay is set for measurement quantities (gasket length); counts fall back to the number.
      return html`${line.quantityDisplay ?? line.quantity}${hint}${info}`;
    }

    // A non-null quantityDisplay is the backend's signal that this quantity is a measurement
    // (length) rather than a count - the same signal the read-only branch above keys on.
    const input = line.quantityDisplay != null ? this.lengthInput(line) : this.countInput(line);
    return html`${input}${hint ? html`<span class="po-quantity-hint">${hint.trimStart()}</span>` : ''}${info}`;
  }

  // Info icon explaining the line's purchasing unit: how much one pack holds (component sold in
  // packs), the roll length (gasket), that a component is sold individually, or that a gasket is
  // not supplied in a roll. Empty when the concept does not apply (extrusions, fills).
  private packRollInfo(line: FabricationPurchaseOrderItem): TemplateResult | '' {
    const packRoll = purchaseOrderItemPackRoll(line);
    if (!packRoll) return '';
    let tip: string;
    if (packRoll.kind === 'pack') tip = LangFabricationPurchaseOrder.packSizeTooltip(packRoll.detail);
    else if (packRoll.kind === 'roll') tip = LangFabricationPurchaseOrder.rollLengthTooltip(packRoll.detail);
    else if (packRoll.kind === 'noPack') tip = LangFabricationPurchaseOrder.componentNotPackedTooltip();
    else tip = LangFabricationPurchaseOrder.gasketNotRolledTooltip();
    return html`&nbsp;<webmodule-tooltip content=${tip} hoist>
        <webmodule-icon library="fa" name="fas-circle-info" color="cyan"></webmodule-icon>
      </webmodule-tooltip>`;
  }

  // Counts (extrusion/component/fill) edit the raw quantity directly as a number.
  private countInput(line: FabricationPurchaseOrderItem): TemplateResult {
    const onChange = async (e: WebmoduleChangeEvent) => {
      const input = e.target as WebmoduleInput;
      const newQty = parseFloat(input.value);
      if (!isFinite(newQty) || newQty <= 0) {
        input.value = `${line.quantity}`;
        return;
      }
      await this.options.onQuantityChange(line, newQty);
    };

    return html`<webmodule-input
      size="small"
      class="po-quantity-input"
      type="number"
      min="0"
      value=${line.quantity}
      @webmodule-change=${onChange}
    ></webmodule-input>`;
  }

  // A measurement quantity (e.g. gasket total length) is edited as a length string, like the
  // optimizer kerf setting: the user types a measurement, V6 parses it to the internal value,
  // and the canonical formatted length is shown back. A plain text input (no number spinners)
  // since the value carries units.
  private lengthInput(line: FabricationPurchaseOrderItem): TemplateResult {
    const onChange = async (e: WebmoduleChangeEvent) => {
      const input = e.target as WebmoduleInput;
      if (!v6SupportsVersion(v6VersionMap.hasMeasurementParsing)) {
        fireQuickErrorToast(LangFabricationPurchaseOrder.lengthEditUnsupported());
        input.value = line.quantityDisplay ?? `${line.quantity}`;
        return;
      }
      const result = await v6Util().parseMeasurement(this.options.supplierId, input.value.trim());
      // A null result means the V6/supplier call itself failed (offline); a result with
      // valid === false means V6 was reached but could not parse the typed text.
      if (!result || !result.valid) {
        fireQuickErrorToast(
          result ? LangFabricationPurchaseOrder.invalidLengthError() : LangFabricationPurchaseOrder.lengthCheckOffline()
        );
        // Revert the field to the last valid length directly (Lit would skip the DOM update
        // since the bound value is unchanged).
        input.value = line.quantityDisplay ?? `${line.quantity}`;
        return;
      }
      // Show the canonical length immediately; the deferred save sends internalValue and the
      // backend re-derives quantityDisplay in the supplier's units.
      line.quantityDisplay = result.displayText;
      await this.options.onQuantityChange(line, result.internalValue);
    };

    return html`<webmodule-input
      size="small"
      class="po-quantity-input"
      type="text"
      value=${line.quantityDisplay ?? line.quantity}
      @webmodule-change=${onChange}
    ></webmodule-input>`;
  }

  private async onMenuItemSelected(event: WebmoduleSelectEvent, line: FabricationPurchaseOrderItem) {
    if (event.detail.item.value === 'remove') {
      if (
        await AskConfirmation(
          LangFabricationPurchaseOrder.removeLineAction(),
          getConfirmationButtons()[ConfirmationButtonType.yesNo]
        )
      ) {
        await this.options.onRemoveLine(line);
      }
    }
  }

  private ellipsisMenu(line: FabricationPurchaseOrderItem): TemplateResult {
    if (this.options.isReadonly()) return html``;

    return html`<webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, line)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        <webmodule-menu-item value="remove">
          <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
          ${LangFabricationPurchaseOrder.removeLineAction()}
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-dropdown>`;
  }
}
